import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn } from './cn';

export interface MenuItemProps {
  /** If set, renders as a router Link; otherwise a button. */
  to?: string;
  onClick?: () => void;
  icon?: ReactNode;
  /** Right-aligned content (e.g. a count badge or shortcut hint). */
  trailing?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}

/** A single row inside a Dropdown panel (replaces antd Menu.Item). */
export function MenuItem({ to, onClick, icon, trailing, active, disabled, danger, children }: MenuItemProps) {
  const cls = cn(
    'flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left text-sm font-medium',
    'transition-colors duration-150',
    // Text colors forced (!) so Ant Design's global <a> color can't override them
    // (MenuItem renders as a router <a> when `to` is set).
    disabled
      ? 'pointer-events-none opacity-45'
      : danger
        ? 'text-danger-500! hover:bg-danger-500/10'
        : active
          ? 'bg-brand-50 text-brand-700! font-semibold dark:bg-brand-900/30 dark:text-brand-300!'
          : 'text-content! hover:bg-brand-50 hover:text-brand-700! dark:hover:bg-brand-900/25 dark:hover:text-brand-300!',
  );
  const inner = (
    <>
      {icon != null && (
        <span className="grid h-4 w-4 shrink-0 place-items-center text-[15px] opacity-80 [&>svg]:size-4">{icon}</span>
      )}
      <span className="flex-1 truncate">{children}</span>
      {trailing != null && <span className="ml-2 shrink-0">{trailing}</span>}
    </>
  );
  if (to && !disabled) {
    return (
      <Link to={to} role="menuitem" className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" role="menuitem" disabled={disabled} onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

/** Thin separator between menu groups (replaces antd Menu divider). */
export function MenuDivider({ className }: { className?: string }) {
  return <div role="separator" className={cn('my-1 h-px bg-line', className)} />;
}

/** Non-interactive small-caps section label inside a menu panel. */
export function MenuLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('px-2.5 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-subtle', className)}>
      {children}
    </div>
  );
}
